import React, { useEffect, useState } from "react";
import { api, type SessionUser } from "../api/client";

interface AdminUser {
  id: string;
  discord_username: string;
  avatar_url: string | null;
  role: "reader" | "contributor" | "admin";
  is_guild_member: number;
  has_required_role: number;
  last_seen_at: string;
}

interface AdminProject {
  id: string;
  name: string;
  repo_owner: string;
  repo_name: string;
}

export function AdminPage({ user }: { user: SessionUser | null }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [r2Status, setR2Status] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  async function loadAll() {
    try {
      const [usersRes, projectsRes, r2Res] = await Promise.all([
        api.admin.listUsers(),
        api.listProjects(),
        api.admin.getR2Status(),
      ]);
      setUsers(usersRes.users);
      setProjects(projectsRes.projects);
      setR2Status(r2Res.status);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  if (user?.role !== "admin") {
    return <p className="error">Réservé aux administrateurs.</p>;
  }

  async function handleRoleChange(userId: string, role: string) {
    try {
      await api.admin.setUserRole(userId, role as any);
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function handleDeleteProject(projectId: string, name: string) {
    if (!window.confirm(`Supprimer définitivement le projet « ${name} » (notes, traductions, historique inclus) ?`)) {
      return;
    }
    try {
      await api.admin.deleteProject(projectId);
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function handleReconcile() {
    setReconciling(true);
    try {
      const { status } = await api.admin.reconcileR2();
      setR2Status(status);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setReconciling(false);
    }
  }

  async function handleOverride(override: "none" | "forced_frozen" | "forced_unfrozen") {
    try {
      await api.admin.setR2Override(override);
      await loadAll();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div className="page">
      <h1>Administration</h1>
      {error && <p className="error">{error}</p>}
      {loading && <p>Chargement…</p>}

      <section>
        <h2>Garde-fou R2</h2>
        {r2Status && (
          <div className="r2-guard">
            <div className="r2-guard__bar">
              <div
                className="r2-guard__bar-fill"
                style={{ width: `${Math.min(100, (r2Status.totalGb / r2Status.freezeThresholdGb) * 100)}%` }}
              />
            </div>
            <p>
              <strong>{r2Status.totalGb.toFixed(2)} Go</strong> stockés sur {r2Status.freezeThresholdGb} Go
              (seuil de gel) — dernière mesure :{" "}
              {r2Status.lastReconciledAt ? new Date(r2Status.lastReconciledAt).toLocaleString() : "jamais"}
              {r2Status.reconcileInProgress && " — réconciliation en cours (bucket volumineux, étalée sur plusieurs nuits)"}
            </p>
            <p>
              Opérations d'écriture ce mois : <strong>{r2Status.monthlyClassAOps}</strong> /{" "}
              {r2Status.monthlyClassALimit}
            </p>
            <p>
              Statut :{" "}
              {r2Status.frozen ? (
                <span className="badge badge--danger">Gelé — {r2Status.frozenReason}</span>
              ) : (
                <span className="badge badge--ok">Actif</span>
              )}
              {r2Status.manualOverride !== "none" && (
                <span className="badge"> (override manuel : {r2Status.manualOverride})</span>
              )}
            </p>
            <div className="r2-guard__actions">
              <button className="btn btn--small" onClick={handleReconcile} disabled={reconciling}>
                {reconciling ? "Recalcul…" : "Recalculer maintenant"}
              </button>
              <button className="btn btn--ghost btn--small" onClick={() => handleOverride("forced_frozen")}>
                Forcer le gel
              </button>
              <button className="btn btn--ghost btn--small" onClick={() => handleOverride("forced_unfrozen")}>
                Forcer le dégel
              </button>
              <button className="btn btn--ghost btn--small" onClick={() => handleOverride("none")}>
                Revenir à l'automatique
              </button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h2>Utilisateurs</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>Discord</th>
              <th>Membre serveur</th>
              <th>Rôle requis</th>
              <th>Rôle site</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.avatar_url && <img className="admin-table__avatar" src={u.avatar_url} alt="" />}</td>
                <td>{u.discord_username}</td>
                <td>{u.is_guild_member ? "✅" : "—"}</td>
                <td>{u.has_required_role ? "✅" : "—"}</td>
                <td>
                  <select value={u.role} onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                    <option value="reader">Lecteur</option>
                    <option value="contributor">Contributeur</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h2>Projets</h2>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Repo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td>
                  {p.repo_owner}/{p.repo_name}
                </td>
                <td>
                  <button className="btn btn--ghost btn--small" onClick={() => handleDeleteProject(p.id, p.name)}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
